import posthog from "posthog-js"

import { ApiResponseError } from "./api/apiUtils"
import { POSTHOG_TEAM_DOMAIN, POSTHOG_TEAM_ID } from "./constants"
import { Capacitor } from "@capacitor/core"
import * as sentry from "@sentry/capacitor"
import {
  browserTracingIntegration,
  init as sentryReactInit,
  SeverityLevel,
} from "@sentry/react"

import { Company } from "./redux/api/me/types"
import { MeResponse, UserGroup } from "./redux/user/types"

/**
 * This file initializes all analytics/statistics we currently support.
 *
 * It also exposes functions to handle user identification
 * and events.
 */

const sentryDNS = import.meta.env.VITE_APP_SENTRY_DSN
const posthogId = import.meta.env.VITE_APP_POSTHOG_ID
const posthogUrl = import.meta.env.VITE_APP_POSTHOG_URL
const hubspotId = import.meta.env.VITE_APP_HUBSPOT_ID
const appVersion = import.meta.env.VITE_APP_VERSION
const environment = import.meta.env.VITE_APP_SENTRY_ENV || "development"

export const isSentryActive = !!sentryDNS
const isPosthogActive = !!posthogId && !!posthogUrl
const isHubspotActive = !!hubspotId

/**
 * Lives next to `isHubspotActive` so the script `AnalyticsProvider` loads and
 * the calls queued here can never disagree about whether HubSpot is enabled.
 * JOAN-11163.
 */
export const hubspotScriptUrl = isHubspotActive
  ? `https://js-eu1.hs-scripts.com/${hubspotId}.js`
  : undefined

/**
 * JOAN-11163: HubSpot's tracking code drains `window._hsq` once it loads, so
 * the queue has to exist before the script does. We used to read it as a
 * "HubSpot is ready" flag instead, which silently dropped `identify` whenever
 * `/me` resolved first and left the session anonymous for HubSpot.
 */
if (isHubspotActive) {
  window._hsq = window._hsq || []
}

/**
 * `/me` is refetched by several mutations, and every refetch used to send
 * another `identify` and `trackPageView` for the same user, inflating page
 * views for a single visit. JOAN-11163.
 */
let hubspotIdentifiedEmail: string | null = null

let tracesSampleRate = environment === "production" ? 0.1 : 1

/**
 * https://app.posthog.com/
 *
 * Event and user tracking.
 */
if (isPosthogActive) {
  console.log("Posthog was initialized.")

  posthog.init(posthogId, {
    api_host: posthogUrl,
    disable_session_recording: true,
    // to use heatmaps the autocapture must be enabled
    autocapture: true,
    /**
     * On native we route fetch through native HTTP (JOAN-10665), which broke
     * PostHog: its compressed payloads don't survive the bridge. Send plain
     * JSON on native so events reach PostHog again (JOAN-10836).
     */
    ...(Capacitor.isNativePlatform() ? { disable_compression: true } : {}),
  })

  /**
   * Attach the build's app version to every event so we can tell which
   * (potentially cached) client version produced it. JOAN-10588.
   */
  posthog.register({ app_version: appVersion })

  const tracesSampleRatePosthog = posthog.getFeatureFlagPayload(
    "tracesSampleRate",
  ) as number | undefined

  tracesSampleRate =
    tracesSampleRatePosthog === undefined ? 0 : tracesSampleRatePosthog

  /**
   * We expose posthog for marketing and Google Tag Manager.
   */
  window.posthog = posthog
}

/**
 * https://sentry.io/
 *
 * Is used for error tracking only.
 */
if (isSentryActive) {
  console.log("Sentry was initialized.")
  /**
   * React sibling init supported officially in @sentry/capacitor v3+.
   * https://docs.sentry.io/platforms/javascript/guides/capacitor/#configure
   */
  sentry.init(
    {
      dsn: sentryDNS,
      environment,
      debug: import.meta.env.DEV,
      release: import.meta.env.VITE_APP_VERSION,
      integrations: [browserTracingIntegration()],
      tracesSampleRate,
    },
    sentryReactInit,
  )
}

/**
 * HubSpot picks which CTAs to render while its script initializes, which is
 * before `/me` resolves and therefore before it knows who the visitor is. CTAs
 * targeted at a contact list never match a logged-in portal user without this
 * second pass. `hsCallsToActionReady` is HubSpot's documented way to defer
 * until the API exists. JOAN-11163.
 * https://developers.hubspot.com/docs/api-reference/latest/marketing/ctas-sdk/guide
 */
const refreshHubspotCtas = () => {
  if (window.HubSpotCallsToActions) {
    window.HubSpotCallsToActions.refresh()
    return
  }

  window.hsCallsToActionReady = window.hsCallsToActionReady || []
  window.hsCallsToActionReady.push(() => {
    window.HubSpotCallsToActions?.refresh()
  })
}

/**
 * Currently supported events, described and updated here:
 *
 * https://docs.google.com/spreadsheets/d/1LlRcY9IH6IFN_nsV9CTp1stSBTX4XC2VnZrxkiVIKmw/edit#gid=507533559
 */
export const analyticsIdentifyUser = (user: MeResponse) => {
  if (!user) {
    const problem = Error(
      "No user found when calling analyticsIdentifyUser. analyticsIdentifyUser() failed.",
    )

    if (isSentryActive) {
      sentry.captureException(problem)
    }

    console.error(problem.message)

    return
  }

  // Handle both array and single company formats
  let company: Company | null = null

  if (user.companies && user.companies.length > 0) {
    company = user.companies[0]
  } else if ((user as any).company) {
    company = (user as any).company as Company
  }

  if (!company) {
    const problem = Error(
      `No company found on the user ${user.id}. analyticsIdentifyUser() failed.`,
    )

    if (isSentryActive) {
      sentry.captureException(problem)
    }

    console.error(problem.message)

    return
  }

  const { email, id: userId, groups = [], first_name, last_name } = user
  const { analytics_domain, country, name, id: companyId } = company

  const portal_user_role = groups?.includes(UserGroup.ADMIN)
    ? UserGroup.ADMIN
    : groups?.includes(UserGroup.OFFICE_MANAGER)
      ? UserGroup.OFFICE_MANAGER
      : UserGroup.USER

  if (isSentryActive) {
    sentry.setUser({ email })
  }

  if (isPosthogActive) {
    posthog.group(POSTHOG_TEAM_ID, companyId, {
      team_name: name,
      team_id: companyId,
      team_domain: analytics_domain,
    })

    if (analytics_domain) {
      posthog.group(POSTHOG_TEAM_DOMAIN, analytics_domain, {
        team_domain: analytics_domain,
      })
    }

    posthog.identify(userId, {
      email,
      team_name: name,
      team_id: companyId,
      team_domain: analytics_domain,
      team_country: country,
      portal_user_role,
    })

    posthog.setPersonProperties({
      email,
      team_name: name,
      team_id: companyId,
      team_domain: analytics_domain,
      team_country: country,
      portal_user_role,
    })
  }

  if (isHubspotActive && hubspotIdentifiedEmail !== email) {
    hubspotIdentifiedEmail = email

    window._hsq.push([
      "identify",
      {
        email,
        id: userId,
        firstname: first_name,
        lastname: last_name,
        company: name,
      },
    ])
    window._hsq.push(["trackPageView"])

    refreshHubspotCtas()
  }
}

export const enablePosthogRecording = (groups?: string[]) => {
  if (!isPosthogActive || !groups) {
    return
  }

  if (
    groups.includes(UserGroup.ADMIN) ||
    groups.includes(UserGroup.OFFICE_MANAGER)
  ) {
    posthog.startSessionRecording()
  }
}

export const resetAnalytics = () => {
  console.log("Resetting analytics")

  if (isPosthogActive) {
    posthog.reset()
  }

  /**
   * HubSpot has no API to un-identify a visitor, and the `hubspotutk` cookie
   * deliberately survives logout so the browser stays the same visitor and
   * keeps its CTA dismissals. Clearing our own marker is enough for the next
   * login to send a fresh `identify`. JOAN-11163.
   */
  hubspotIdentifiedEmail = null
}

/**
 * Currently supported events, described and updated here:
 *
 * https://docs.google.com/spreadsheets/d/1LlRcY9IH6IFN_nsV9CTp1stSBTX4XC2VnZrxkiVIKmw/edit#gid=773844549
 */
export enum SupportedEvents {
  AMENITY_ADD = "JAP/admin/amenity_add",
  AMENITY_DELETE = "JAP/admin/amenity_delete",
  ANNOUNCEMENT_MARKED_AS_READ = "JAP/user/announcement_marked_as_read",
  ANNOUNCEMENT_VIEWED = "JAP/user/announcement_viewed",
  ASSET_ADD = "JAP/admin/asset_add",
  ASSET_BOOKED = "JAP/user/asset_booked",
  ASSET_BOOKED_FOR_OTHERS = "JAP/admin/asset_booked_for_others",
  ASSET_DELETE = "JAP/admin/asset_delete",
  ASSET_TYPE_ADD = "JAP/admin/asset_type_add",
  ASSET_TYPE_DELETE = "JAP/admin/asset_type_delete",
  ASSET_TYPE_UPDATE = "JAP/admin/asset_type_update",
  ASSET_UPDATE = "JAP/admin/asset_update",
  BOOKING_CONFIRMED = "JAP/user/booking_confirmed",
  DEPARTMENT_ADD = "JAP/admin/department_add",
  DEPARTMENT_DELETE = "JAP/admin/department_delete",
  DEPARTMENT_UPDATE = "JAP/admin/department_update",
  DESK_ADD = "JAP/admin/desk_add",
  DESK_DELETE = "JAP/admin/desk_delete",
  DESK_UPDATE = "JAP/admin/desk_update",
  FILTER_BY_AMENITY = "JAP/user/filter_by_amenity",
  FLOOR_ADD = "JAP/admin/floor_add",
  FLOOR_DELETE = "JAP/admin/floor_delete",
  FLOOR_UPDATE = "JAP/admin/floor_update",
  FLOORPLAN_ADD = "JAP/admin/floorplan_add",
  FLOORPLAN_EDIT = "JAP/admin/floorplan_edit",
  FLOORPLAN_RESOURCE_ADD = "JAP/admin/floorplan_resource_add",
  FLOORPLAN_RESOURCE_REMOVE = "JAP/admin/floorplan_resource_remove",
  FLOORPLAN_SHARING_DISABLED = "JAP/admin/floorplan_sharing_disabled",
  FLOORPLAN_SHARING_ENABLED = "JAP/admin/floorplan_sharing_enabled",
  INITIAL_LOCATION_ADD = "JAP/admin/initial_location_add",
  LOCATION_ADD = "JAP/admin/location_add",
  LOCATION_DELETE = "JAP/admin/location_delete",
  LOCATION_UPDATE = "JAP/admin/location_update",
  PEOPLE_ADD = "JAP/admin/people_add",
  PEOPLE_REMOVE = "JAP/admin/people_remove",
  RESERVATION_ADD = "JAP/user/reservation_add",
  DESK_RESERVATION_DELETED = "JAP/user/desk_reservation_deleted",
  RESERVATION_UPDATE = "JAP/user/reservation_update",
  USER_LOGGED_IN = "JAP/login/user_logged_in",
  USER_SIGNED_UP = "JAP/login/user_signed_up",
  TMP_WEB_USER_SIGN_UP = "TMP/login/web_user_sign_up",
  DESK_BOOKED = "JAP/user/desk_booked",
  DESK_BOOKED_FOR_OTHERS = "JAP/admin/desk_booked_for_others",
  DESK_RESERVATION_CHECKIN = "JAP/user/desk_reservation_checkin",
  DESK_RESERVATION_CHECKOUT = "JAP/user/desk_reservation_checkout",
  DESK_PRESENCE = "JMA/user/desk_presence",
  DESK_MAP = "JMA/user/desk_map",
  DESK_MAP_TIMESLIDER = "JMA/user/desk_map_timeslider",
  DESK_RESERVATION_RECOMMENDED = "JMA/user/desk_reservation_recommended",
  NEW_APP_FEEDBACK_SUBMITTED = "JMA/user/new_app_feedback_submitted",
  APP_INTERFACE_SWITCHED = "JMA/user/app_interface_switched",
  NEW_APP_POPUP_DISMISSED = "JMA/user/new_app_popup_dismissed",
  VISITOR_SOLUTION_ENABLED = "JAP/admin/all_visitor_solution_enabled",
  VISITOR_SOLUTION_DISABLED = "JAP/admin/all_visitor_solution_disabled",
  VISITOR_ADMIN_MANAGEMENT_ENABLED = "JAP/admin/visitor_solution_enabled",
  VISITOR_ADMIN_MANAGEMENT_DISABLED = "JAP/admin/visitor_solution_disabled",
  VISITOR_ADMIN_DOCUMENT_ADD = "JAP/admin/visitor_document_add",
  VISITOR_ADMIN_DOCUMENT_DELETE = "JAP/admin/visitor_document_delete",
  VISITOR_ADMIN_TABLET_DELETE = "JAP/admin/visitor_tablet_delete",
  VISITOR_ADMIN_PRINTER_DELETE = "JAP/admin/visitor_printer_delete",
  VISITOR_ADMIN_TABLET_ADD = "JAP/admin/visitor_tablet_add",
  VISITOR_ADMIN_PRINTER_ADD = "JAP/admin/visitor_printer_add",
  VISITOR_ADMIN_SIGN_IN_ENABLED = "JAP/admin/visitor_tablet_signin_enabled",
  VISITOR_ADMIN_SIGN_IN_DISABLED = "JAP/admin/visitor_tablet_signin_disabled",
  VISITOR_ADMIN_PRINTER_ENABLED = "JAP/admin/visitor_printer_enabled",
  VISITOR_ADMIN_PRINTER_DISABLED = "JAP/admin/visitor_printer_disabled",
  VISITOR_ADMIN_INVITE_ADD = "JAP/admin/visitor_invite_add",
  VISITOR_ADMIN_INVITE_UPDATE = "JAP/admin/visitor_invite_update",
  VISITOR_ADMIN_VISIT_ADD = "JAP/admin/visitor_visit_add",
  VISITOR_ADMIN_VISIT_UPDATE = "JAP/admin/visitor_visit_update",
  VISITOR_USER_INVITE_ADD = "JAP/user/visitor_invite_add",
  VISITOR_USER_INVITE_UPDATE = "JAP/user/visitor_invite_update",
  VISITOR_USER_VISIT_ADD = "JAP/user/visitor_visit_add",
  VISITOR_USER_VISIT_UPDATE = "JAP/user/visitor_visit_update",
  VISITOR_CHECK_IN = "JMA/visitor/visitor_visitor_check_in",
  VISITOR_DOCUMENT_SIGNED = "JMA/visitor/visitor_visitor_document_signed",
  VISITOR_USER_OPEN_VISIT_CARD = "JMA/user/visitor_open_visit_card",
  VISITOR_USER_OPEN_INVITE_CARD = "JMA/user/visitor_open_invite_card",
  VISITOR_USER_BADGE_PRINTED = "JMA/user/visitor_badge_printed",
  QR_NFC_CODE_SCANNED = "JAP/user/QR_NFC_code_scanned",
  NEW_BUSINESS_PLAN_SELECTED = "JAP/admin/new_business_plan_selected",
  LOCATION_TRACKING_DISABLED = "JAP/user/location_tracking_disabled",
  LOCATION_TRACKING_ENABLED = "JAP/user/location_tracking_enabled",
  NOTIFICATION_BELL_ICON_CLICK = "JAP/user/notification_bell_icon_click",
  NOTIFICATIONS_MARK_AS_READ = "JAP/user/notifications_mark_as_read",
  NOTIFICATIONS_SHOW_ALL = "JAP/user/notifications_show_all",
  NOTIFICATIONS_BUTTON_CLICK = "JAP/user/notifications_button_click",
  NOTIFICATIONS_SELECT_TYPE = "JAP/user/notifications_select_type",
  NOTIFICATIONS_ONLY_SHOW_UNREAD = "JAP/user/notifications_only_show_unread",

  ROOM_BOOKING_BOOKED = "JAP/user/room_booked",
  ROOM_BOOKING_BOOKED_FOR_OTHER = "JAP/user/room_booking_booked_for_other",
  ROOM_BOOKING_CANCEL = "JAP/user/room_booking_cancel",
  ROOM_BOOKING_CHECKIN = "JAP/user/room_booking_checkin",
  ROOM_BOOKING_CLICK_DETAILS = "JAP/user/room_booking_click_details",
  ROOM_BOOKING_CLICK_FREE = "JAP/user/room_booking_click_free",
  ROOM_BOOKING_DELETE = "JAP/user/room_booking_delete",
  ROOM_BOOKING_EDIT_RESERVATION = "JAP/user/room_booking_edit_reservation",
  ROOM_BOOKING_END = "JAP/user/room_booking_end",
  ROOM_BOOKING_EXTEND = "JAP/user/room_booking_extend",
  ROOM_BOOKING_SWITCH_VIEW = "JAP/user/room_booking_switch_view",
  EPAPER_BADGE_CLEARED = "JAP/user/visitor_badge_clear",
  EMPLOYEE_BADGE_SYNC_SUCCESS = "JMA/admin/employee_badge_sync_success",
  EMPLOYEE_BADGE_SYNC_FAILED = "JMA/admin/employee_badge_sync_failed",
}

export const PLATFORM_TYPES = {
  WEB: "web",
  MOBILE: "mobile",
  DEVICE: "device",
} as const

export const RESERVATION_TYPES = {
  ASSET: "asset",
  DESK: "desk",
  ROOM: "room",
} as const

export const FLOORPLAN_RESOURCE_TYPES = {
  ASSET: "asset",
  DESK: "desk",
  ROOM: "room",
} as const

type AnalyticsEventOptions = {
  /**
   * Skip PostHog's batched queue and send on this call. The queue flushes on a
   * timer, and the beacon it sends when the page unloads is dropped by the
   * native WebView, so an event captured immediately before a reload is lost.
   */
  sendInstantly?: boolean
}

/**
 * How long a `sendInstantly` request needs before a reload. The native
 * WebView drops it if the document tears down while it is still in flight.
 */
export const ANALYTICS_RELOAD_DELAY_MS = 500

export const analyticsEvent = (
  eventName: SupportedEvents,
  metadata?: any,
  { sendInstantly }: AnalyticsEventOptions = {},
) => {
  if (!isPosthogActive) {
    return
  }

  try {
    posthog.capture(
      eventName,
      metadata,
      sendInstantly ? { send_instantly: true } : undefined,
    )
  } catch (error) {
    // Tracking must not fail mutation success toasts or cleanup
    if (isSentryActive) {
      sentry.captureException(error)
    }
  }
}

export const analyticsSetPersonProperties = (
  properties: Record<string, string | number | boolean | null>,
) => {
  if (!isPosthogActive) {
    return
  }

  posthog.setPersonProperties(properties)
}

export const logBreadcrumb = (
  error: ApiResponseError | string,
  level: SeverityLevel = "info",
) => {
  if (!isSentryActive) {
    return
  }

  if (typeof error === "string") {
    sentry.addBreadcrumb({
      message: error,
      level,
    })
    return
  }

  if (error?.message) {
    sentry.addBreadcrumb({
      message: error.message,
      level,
    })
    return
  }

  sentry.addBreadcrumb({
    message: JSON.stringify(error),
    level,
  })
}
